import React from "react";
import { cn } from "../../util/cn";

type SpinnerSize = "xs" | "sm" | "md" | "lg" | "xl";

export interface SpinnerProps {
  size?: SpinnerSize;
  className?: string;
  ariaLabel?: string;
  borderWidth?: 2 | 4;
}

const sizeClasses: Record<SpinnerSize, string> = {
  xs: "h-4 w-4",
  sm: "h-6 w-6",
  md: "h-8 w-8",
  lg: "h-10 w-10",
  xl: "h-12 w-12",
};

export const Spinner: React.FC<SpinnerProps> = ({
  size = "md",
  className,
  ariaLabel = "Loading",
  borderWidth = 2,
}) => {
  const borderClass = borderWidth === 4 ? "border-4" : "border-2";
  return (
    <div className="flex items-center justify-center" role="status" aria-live="polite" aria-busy="true">
      <div
        className={cn(
          "animate-spin rounded-full border-primary border-t-transparent",
          sizeClasses[size],
          borderClass,
          className
        )}
      />
      <span className="sr-only">{ariaLabel}</span>
    </div>
  );
};

export interface SpinnerOverlayProps {
  fullScreen?: boolean;
  blur?: boolean;
  backgroundClassName?: string;
  className?: string;
  ariaLabel?: string;
  spinnerSize?: SpinnerSize;
  borderWidth?: 2 | 4;
}

// Overlay that centers a spinner and dims the background. Use inside a relative container or in fullScreen mode.
export const SpinnerOverlay: React.FC<SpinnerOverlayProps> = ({
  fullScreen = false,
  blur = true,
  backgroundClassName,
  className,
  ariaLabel = "Loading",
  spinnerSize = "md",
  borderWidth = 2,
}) => {
  return (
    <div
      className={cn(
        fullScreen ? "fixed inset-0 z-50" : "absolute inset-0 z-10",
        "flex items-center justify-center",
        blur ? "bg-background/60 backdrop-blur-[1px]" : "bg-background/60",
        className,
        backgroundClassName
      )}
      aria-live="polite"
      aria-busy="true"
    >
      <Spinner size={spinnerSize} ariaLabel={ariaLabel} borderWidth={borderWidth} />
    </div>
  );
};

export default Spinner;
